import type { GetStaffMeResponse } from "@wallyo/contracts/console";
import type { ScreenBadge } from "@wallyo/contracts/console/overview";
import type { DepartmentId } from "@wallyo/contracts/workspaces";

import { t } from "../i18n";
import { formatNumber } from "../i18n/format";
import { UNIVERSAL_SCREEN_IDS, screenHref, sidebarScreens, visibleScreens, type Screen } from "../navigation/screens";

/**
 * The workspace's own screens, then the block every employee gets
 * (WALLIYO-0132).
 *
 * It used to be two groups, `main` and Configuration, and Configuration had
 * become a junk drawer: Human Resources sat in it next to Rules and Audit.
 * Now the sidebar shows one department at a time, and the header says which.
 */
export function Sidebar({
  me,
  workspace,
  current,
  counts,
  hash,
}: {
  me: GetStaffMeResponse;
  /** Null while somebody holds no workspace at all: only the shared block shows. */
  workspace: DepartmentId | null;
  current: Screen | null;
  /** What each row says beside its name. Absent until the first load answers. */
  counts?: Readonly<Record<string, ScreenBadge>>;
  /** The route, so a row pointing at one tab of a screen knows if it is open. */
  hash?: string;
}) {
  const visible = visibleScreens(me.features);
  // In the order the design lists them — your leave, then what you asked for —
  // not the order the catalogue happens to hold them in.
  const universal = UNIVERSAL_SCREEN_IDS.flatMap((id) => visible.filter((screen) => screen.id === id));
  // Whatever the workspace lists and this person can open, less anything the
  // shared block already shows: one row per screen, never two.
  const owned = sidebarScreens(workspace, me.features).filter(
    (screen) => !(UNIVERSAL_SCREEN_IDS as readonly string[]).includes(screen.id),
  );

  /**
   * The one row the hash is on: the **longest** href it starts with.
   *
   * `#hr/employees/payroll` starts with `#hr/employees` as well, so a plain
   * prefix test lit Employees and Payroll at once. The most specific row wins,
   * which is the only reading that can be right when one row's destination sits
   * inside another's (WALLIYO-0134).
   */
  const hrefOf = (screen: Screen) => screen.href ?? screenHref(screen.id);
  const here = (hash ?? "").split("?")[0] ?? "";
  const openHref = [...owned, ...visible]
    .map(hrefOf)
    .filter((href) => here === href || here.startsWith(`${href}/`))
    .sort((a, b) => b.length - a.length)[0];

  const link = (screen: Screen) => {
    // A workspace can list several tabs of one screen, so the open row is the
    // one whose own href the hash matches — comparing screen ids would light
    // all of them at once (WALLIYO-0134).
    const href = hrefOf(screen);
    // A row with its own destination is current when the hash is on it; a plain
    // row falls back to the resolved screen, for hashes carrying a query or an id.
    const on = openHref ? href === openHref : screen.id === current?.id;
    const badge = counts?.[screen.id];
    return (
      <a key={href} href={href} className={on ? "on" : ""} aria-current={on ? "page" : undefined}>
        {t(screen.label)}
        <span className="grow" />
        {/* The badge is why the design has one: four cases breaching is visible
            from any screen, without opening the Overview to find out. A screen
            just built says so instead, until everybody has seen it. */}
        {badge ? (
          <span className={badge.tone === "plain" ? "nav-count mono" : `nav-pill ${badge.tone}`}>
            {formatNumber(badge.count)}
          </span>
        ) : screen.isNew ? (
          <span className="nav-new">{t("nav.new")}</span>
        ) : null}
      </a>
    );
  };

  return (
    <nav aria-label={t("nav.aria")}>
      <div className="nav">{owned.map(link)}</div>
      {/* Yours, not the department's, so it follows you between workspaces:
          who works here, and what you have asked to be allowed to do. */}
      <div className="nav-rule" aria-hidden="true" />
      <div className="nav sub">{universal.map(link)}</div>
    </nav>
  );
}
